import type { IsoDateTime } from './common';
import { CHANNEL_INFO, type MarketplaceChannel } from './marketplace';
import type { SellerRetention } from './privacy';

// Sistem durumu: what the background side of the warehouse is doing, for supervisors.

/** Background job topics in Turkish, as the status screen names them. */
export const JOB_TOPIC_LABELS: Readonly<Record<string, string>> = {
  'marketplace.sync_all': 'Pazaryeri senkron zamanlayıcısı',
  'marketplace.sync_orders': 'Pazaryeri sipariş senkronu',
  'marketplace.refresh_order': 'Pazaryeri sipariş güncellemesi',
  'channel.push': 'Pazaryerine durum bildirimi',
  'shipping.prepare_label': 'Kargo etiketi hazırlama',
  'shipping.cancel_label': 'Kargo kaydı iptali',
  'shipping.redraw_label': 'Kargo etiketinin yeniden çizilmesi',
  'documents.request': 'e-Belge talebi',
  'billing.order_charges': 'Sipariş hizmet ücretleri',
  'billing.storage_daily': 'Günlük depolama ücreti',
  'billing.invoices': 'Aylık hizmet faturaları',
  'orders.allocate': 'Stok rezervasyonu',
  'printing.dispatch': 'Ağ yazıcısına gönderim',
  'ops.check': 'Sistem kontrolü ve uyarılar',
  'marketplace.stock_changed': 'Stok değişikliğinin pazaryerine gönderimi',
  'marketplace.push_stock': 'Pazaryerine stok gönderimi',
  'marketplace.push_stock_all': 'Stok gönderimi zamanlayıcısı',
  'marketplace.check_stock_batch': 'Pazaryeri stok güncellemesinin sonucu',
  'carriers.track': 'Kargo takibi (Aras)',
  'privacy.erase': 'Alıcıların kişisel verilerinin silinmesi (KVKK)',
  'orders.stock_waiting_digest': 'Stok bekleyen siparişler özeti (Telegram)',
  'floor.urgent_alert': 'Acil işin Telegram / webhook bildirimi',
  'finance.sync_all': 'Pazaryeri finans kayıtları zamanlayıcısı (Kârlılık)',
  'finance.sync_account': 'Pazaryeri finans kayıtlarının okunması (Kârlılık)',
};

export const OPS_SEVERITIES = ['critical', 'warning'] as const;
export type OpsSeverity = (typeof OPS_SEVERITIES)[number];

export const OPS_PROBLEM_KINDS = ['worker', 'queue', 'keyring', 'marketplace', 'carrier', 'job', 'print'] as const;
export type OpsProblemKind = (typeof OPS_PROBLEM_KINDS)[number];

/** One thing that needs a look; key is stable while the problem lasts (alerts use it). */
export interface OpsProblem {
  readonly key: string;
  readonly severity: OpsSeverity;
  readonly kind: OpsProblemKind;
  /** Turkish, one line */
  readonly title: string;
  /** The error the job or the marketplace reported */
  readonly detail: string | null;
  readonly since: IsoDateTime;
  readonly occurrences: number | null;
  readonly tenantCode: string | null;
  readonly orderId: string | null;
  readonly orderNumber: string | null;
  readonly credentialId: string | null;
  /** Set for failed jobs: the row a supervisor retries or dismisses */
  readonly failureId: string | null;
  /** A supervisor sent the job again; waiting for the outcome */
  readonly retrying: boolean;
}

export interface OpsWorker {
  readonly name: string;
  readonly startedAt: IsoDateTime;
  readonly seenAt: IsoDateTime;
  readonly alive: boolean;
}

export interface OpsMarketplace {
  readonly id: string;
  readonly tenantCode: string;
  readonly channel: string;
  readonly label: string;
  readonly status: string;
  /** Orders are read up to this moment */
  readonly syncedUntil: IsoDateTime | null;
  /** When an order pull last went through (the read window may start earlier: intake started, a list read again) */
  readonly pulledAt: IsoDateTime | null;
  /** When the seller connected the account (a never-synced account is quiet since then) */
  readonly connectedAt: IsoDateTime;
  readonly lastError: string | null;
  /** No pull went through for longer than OPS_LIMITS.syncStaleMinutes while the account is active */
  readonly stale: boolean;
  /** false: connected, but the seller has not started order intake yet */
  readonly takingOrders: boolean;
}

export interface OpsRecentFailure {
  readonly id: string;
  readonly topic: string;
  readonly title: string;
  readonly status: 'resolved' | 'dismissed';
  readonly lastError: string;
  readonly occurrences: number;
  readonly lastFailedAt: IsoDateTime;
  readonly closedAt: IsoDateTime;
}

/** The credential vault's keys as fingerprints (never the keys), to compare with the backup copy. */
export interface OpsKeyring {
  readonly activeKeyId: string;
  readonly keys: readonly { readonly id: string; readonly fingerprint: string; readonly secrets: number }[];
  /** Key ids the database uses but the running keyring lacks */
  readonly missingKeys: readonly { readonly id: string; readonly secrets: number }[];
}

export interface OpsStatus {
  readonly checkedAt: IsoDateTime;
  /** null when the server runs without the vault (tests) */
  readonly keyring: OpsKeyring | null;
  readonly worker: OpsWorker | null;
  readonly queue: {
    /** Jobs waiting to be handed to the worker */
    readonly pending: number;
    /** …of which waited longer than OPS_LIMITS.queueDelayMinutes */
    readonly delayed: number;
  };
  readonly marketplaces: readonly OpsMarketplace[];
  readonly problems: readonly OpsProblem[];
  /** Closed in the last 24 hours, newest first */
  readonly recent: readonly OpsRecentFailure[];
  /** KVKK: the last run of the erasure of buyers' personal data; null until it first runs */
  readonly privacy: OpsPrivacy | null;
}

/** The daily erasure of buyers' personal data (privacy.erase), as the status screen shows it. */
export interface OpsPrivacy {
  readonly lastRunAt: IsoDateTime;
  /** days a closed order kept its buyer's data on that run (PII_RETENTION_DAYS) */
  readonly retentionDays: number;
  /** orders whose buyer data that run erased */
  readonly orders: number;
  /** the sellers with a period of their own on that run (codes and days), by code */
  readonly sellers: readonly SellerRetention[];
}

/** When quiet becomes a problem. */
export const OPS_LIMITS = {
  /** The worker writes its heartbeat every 30 s */
  heartbeatStaleSeconds: 120,
  queueDelayMinutes: 5,
  /** Orders are synced every 2 minutes */
  syncStaleMinutes: 15,
  /** A job failing longer than this is critical even while the queue still retries it */
  jobCriticalMinutes: 10,
  printPendingMinutes: 5,
  /** Kargo takibi runs every 15 minutes: an hour of failed runs is critical */
  carrierTrackingCriticalMinutes: 60,
} as const;

/**
 * Why a marketplace account's orders are not read: its keys were refused (auth_failed), it was closed
 * (disabled), or no pull succeeded for OPS_LIMITS.syncStaleMinutes (stale, as Sistem durumu counts it).
 */
export const CHANNEL_SYNC_PROBLEMS = ['auth_failed', 'disabled', 'stale'] as const;
export type ChannelSyncProblem = (typeof CHANNEL_SYNC_PROBLEMS)[number];

/**
 * On an open order of such an account (app.channel_sync_warning, migration 0062): a cancel made on the
 * marketplace is not seen while it lasts, so the packing desk and Sevkiyat warn before it leaves.
 */
export interface ChannelSyncWarning {
  readonly problem: ChannelSyncProblem;
  readonly channel: MarketplaceChannel;
  /** the account's name in the panel */
  readonly label: string;
  /** when an order pull last went through (null: never), as Sistem durumu measures a quiet account */
  readonly pulledAt: IsoDateTime | null;
}

/** "Trendyol mağazası: anahtar reddedildi, siparişler çekilmiyor" */
export function channelSyncWarningText(w: ChannelSyncWarning, now: number = Date.now()): string {
  const channel = CHANNEL_INFO[w.channel]?.name ?? w.channel;
  const account = w.label.toLocaleLowerCase('tr').includes(channel.toLocaleLowerCase('tr')) ? w.label : `${channel} · ${w.label}`;
  if (w.problem === 'auth_failed') return `${account}: pazaryeri anahtarı reddedildi, siparişler çekilmiyor`;
  if (w.problem === 'disabled') return `${account}: hesap kapatıldı, siparişler çekilmiyor`;
  const minutes = w.pulledAt ? Math.floor((now - Date.parse(w.pulledAt)) / 60_000) : null;
  return minutes !== null && minutes > 0 ? `${account}: ${minutes} dakikadır sipariş çekilemiyor` : `${account}: siparişler çekilemiyor`;
}

/** What the floor does about it. */
export const CHANNEL_SYNC_ADVICE = 'Pazaryerindeki iptal görülemiyor: kargoya vermeden önce vardiya sorumlusuna sorun, o da satıcıyla kontrol etsin.';

/** GET /health/system: for an outside uptime monitor (no auth, nothing tenant-specific). */
export interface SystemHealth {
  readonly ok: boolean;
  readonly database: boolean;
  readonly worker: { readonly alive: boolean; readonly seenAt: IsoDateTime | null };
  readonly queue: { readonly delayed: number };
}
